import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogIn, LayoutDashboard, LogOut, Mail, ChevronDown,
  BookOpen, Target, Compass, FileText, Users, UserPlus, Building2, Heart } from "lucide-react";
import Logo from "./Logo";
import LiveBadge from "./LiveBadge";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const aboutLinks = [
  { to: "/a-propos", label: "À propos", desc: "Notre histoire & nos valeurs", icon: BookOpen },
  { to: "/vision", label: "Vision & Mission", desc: "Notre ambition pour le Congo", icon: Target },
  { to: "/axes", label: "Nos axes prioritaires", desc: "Citoyenneté · Emploi · Souveraineté", icon: Compass },
  { to: "/statuts", label: "Statuts", desc: "Cadre juridique du mouvement", icon: FileText },
  { to: "/organisation", label: "Organisation (CA + Bureau)", desc: "Gouvernance & structure", icon: Building2 },
  { to: "/equipe", label: "Notre équipe", desc: "Les visages du mouvement", icon: Users },
  { to: "/adhesion", label: "Adhésion", desc: "Rejoindre A.F.B.", icon: UserPlus },
];

const links = [
  { to: "/", label: "Accueil" },
  { type: "dropdown", label: "Le Mouvement", children: aboutLinks },
  { to: "/actions", label: "Nos actions" },
  { to: "/carte", label: "Diaspora" },
  { to: "/actualites", label: "Actualités" },
  { to: "/evenements", label: "Événements" },
  { to: "/medias", label: "Médias" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutMobileOpen, setAboutMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setAboutOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => { setAboutOpen(false); setOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const isAboutActive = aboutLinks.some((l) => location.pathname === l.to);

  return (
    <header className="sticky top-0 z-40" data-testid="navbar">
      {/* Top announcement bar */}
      <div className="bg-[#0A1F44] text-white text-[11px] md:text-xs">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[#D4AF37]">★</span>
            <span className="tracking-wide">Pour un Congo fort, uni et prospère</span>
          </div>
          <a href="mailto:contact@afb-rdc.cd" className="hidden sm:flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors">
            <Mail size={12} /> contact@afb-rdc.cd
          </a>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b border-black/5 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 md:px-8 py-3">
          <Link to="/" data-testid="nav-logo-link" className="shrink-0 flex items-center gap-3">
            <Logo variant="badge" size={56} />
            <div className="hidden md:block leading-tight">
              <div className="font-display font-bold text-sm md:text-base text-[#0A1F44]">LES AMIS DE FATSHI BÉTON</div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#CE1126]">Section Diaspora</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6" data-testid="nav-desktop">
            {links.map((l) => {
              if (l.type === "dropdown") {
                return (
                  <div key="about" className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setAboutOpen((s) => !s)}
                      data-testid="nav-link-about-dropdown"
                      className={`relative inline-flex items-center gap-1 text-[13px] font-medium transition-colors py-2 ${
                        isAboutActive || aboutOpen ? "text-[#0052CC]" : "text-[#0A0F1A]/80 hover:text-[#0052CC]"
                      }`}
                    >
                      {l.label}
                      <ChevronDown size={14} className={`transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
                      {isAboutActive && <span className="absolute -bottom-0.5 left-0 right-4 h-0.5 bg-[#CE1126]" />}
                    </button>
                    {aboutOpen && (
                      <div
                        className="absolute left-0 top-full mt-3 w-[640px] bg-white rounded-xl shadow-2xl border border-black/5 p-4 grid grid-cols-2 gap-1 z-50"
                        data-testid="nav-dropdown-about"
                      >
                        {l.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-dropdown-${c.to.slice(1)}`}
                            className={({ isActive }) =>
                              `flex items-start gap-3 p-3 rounded-lg transition-colors ${
                                isActive ? "bg-[#D4AF37]/10" : "hover:bg-[#F8F9FB]"
                              }`
                            }
                          >
                            <div className="w-9 h-9 rounded-md bg-[#0052CC]/10 grid place-items-center text-[#0052CC] shrink-0">
                              <c.icon size={16} />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-[#0A1F44]">{c.label}</div>
                              <div className="text-xs text-[#475569] mt-0.5 leading-relaxed">{c.desc}</div>
                            </div>
                          </NavLink>
                        ))}
                        <div className="col-span-2 mt-2 pt-3 border-t border-black/5 flex items-center justify-between">
                          <span className="text-xs text-[#475569]">Le mouvement A.F.B. — fondé le 18 nov. 2018</span>
                          <Link to="/dons" className="text-xs font-semibold text-[#D4AF37] hover:underline inline-flex items-center gap-1.5">
                            <Heart size={12} fill="#D4AF37" /> Soutenir
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={l.to} to={l.to}
                  data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `relative text-[13px] font-medium transition-colors py-2 ${
                      isActive ? "text-[#0052CC]" : "text-[#0A0F1A]/80 hover:text-[#0052CC]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {l.label}
                      {isActive && <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-[#CE1126]" />}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <LiveBadge />
            <div className="hidden xl:flex items-center gap-2">
            {user ? (
              <>
                <Button asChild className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm font-bold uppercase tracking-wide text-[11px] px-5" data-testid="nav-dashboard-btn">
                  <Link to="/dashboard"><LayoutDashboard size={14} className="mr-2" /> Espace Membre</Link>
                </Button>
                <Button onClick={handleLogout} variant="ghost" size="sm" className="text-[#0A0F1A]/65" data-testid="nav-logout-btn">
                  <LogOut size={14} />
                </Button>
              </>
            ) : (
              <Button asChild className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm font-bold uppercase tracking-wide text-[11px] px-5" data-testid="nav-login-btn">
                <Link to="/login"><LogIn size={14} className="mr-2" /> Espace Membre</Link>
              </Button>
            )}
            </div>
          </div>

          <button className="xl:hidden p-2 rounded-md hover:bg-black/5" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden border-t border-black/5 bg-white" data-testid="nav-mobile-menu">
            <div className="px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
              {links.map((l) => {
                if (l.type === "dropdown") {
                  return (
                    <div key="m-about">
                      <button
                        onClick={() => setAboutMobileOpen((s) => !s)}
                        className="w-full flex items-center justify-between py-2.5 text-sm font-semibold text-[#0A1F44]"
                        data-testid="nav-mobile-about-toggle"
                      >
                        {l.label}
                        <ChevronDown size={14} className={`transition-transform ${aboutMobileOpen ? "rotate-180" : ""}`} />
                      </button>
                      {aboutMobileOpen && (
                        <div className="pl-3 border-l-2 border-[#D4AF37] space-y-0.5 mb-2">
                          {l.children.map((c) => (
                            <NavLink
                              key={c.to} to={c.to} onClick={() => setOpen(false)}
                              data-testid={`nav-mobile-link-${c.to.slice(1)}`}
                              className={({ isActive }) =>
                                `block py-2 pl-3 text-sm ${isActive ? "text-[#0052CC] font-semibold" : "text-[#475569]"}`
                              }
                            >
                              {c.label}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={l.to} to={l.to} onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
                    className={({ isActive }) => `block py-2.5 text-sm font-medium ${isActive ? "text-[#0052CC]" : "text-[#0A0F1A]/80"}`}
                  >
                    {l.label}
                  </NavLink>
                );
              })}
              <div className="flex flex-col gap-2 pt-3 border-t border-black/5 mt-3">
                {user ? (
                  <Button asChild className="w-full bg-[#0052CC] text-white"><Link to="/dashboard" onClick={() => setOpen(false)}>Espace Membre</Link></Button>
                ) : (
                  <Button asChild className="w-full bg-[#0052CC] text-white"><Link to="/login" onClick={() => setOpen(false)}>Espace Membre</Link></Button>
                )}
                <Button asChild variant="outline" className="w-full"><Link to="/dons" onClick={() => setOpen(false)}>Faire un don</Link></Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
